import type { Timestamp } from '@mct/shared';

export type GanttZoom = 'day' | 'week' | 'month';

export const ZOOM_DAY_WIDTH: Record<GanttZoom, number> = {
  day: 28,
  week: 12,
  month: 4,
};

export const ZOOM_LABEL: Record<GanttZoom, string> = {
  day: '日',
  week: '週',
  month: '月',
};

const MS_PER_DAY = 86_400_000;

export function startOfDayUTC(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

export function diffDays(from: Date, to: Date): number {
  return Math.round(
    (startOfDayUTC(to).getTime() - startOfDayUTC(from).getTime()) / MS_PER_DAY,
  );
}

export function addDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

export function tsToDate(ts: Timestamp | null | undefined): Date | null {
  if (!ts) return null;
  return ts.toDate();
}

/** x-offset (px) for a given date relative to the gantt origin. */
export function dayToX(date: Date, origin: Date, zoom: GanttZoom): number {
  return diffDays(origin, date) * ZOOM_DAY_WIDTH[zoom];
}

/** Compute { x, width } for a bar spanning [start, end]. Inclusive end. */
export function barRect(
  start: Date,
  end: Date,
  origin: Date,
  zoom: GanttZoom,
): { x: number; width: number } {
  const x = dayToX(start, origin, zoom);
  const width = Math.max(
    ZOOM_DAY_WIDTH[zoom],
    dayToX(addDays(end, 1), origin, zoom) - x,
  );
  return { x, width };
}

/** Range spanning all non-null date fields across items, with padding. */
export function computeRange(
  dates: ReadonlyArray<Date | null | undefined>,
  padDays = 7,
): { start: Date; end: Date } | null {
  let min: number | null = null;
  let max: number | null = null;
  for (const d of dates) {
    if (!d) continue;
    const t = d.getTime();
    if (min == null || t < min) min = t;
    if (max == null || t > max) max = t;
  }
  if (min == null || max == null) return null;
  return {
    start: addDays(startOfDayUTC(new Date(min)), -padDays),
    end: addDays(startOfDayUTC(new Date(max)), padDays),
  };
}

export function chartPixelWidth(
  start: Date,
  end: Date,
  zoom: GanttZoom,
): number {
  return (diffDays(start, end) + 1) * ZOOM_DAY_WIDTH[zoom];
}
